import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import {
  Box, TextField, Button, Typography,
  CircularProgress, Alert, InputAdornment, IconButton, Divider,
} from "@mui/material";
import EmailOutlinedIcon           from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon            from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon      from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon   from "@mui/icons-material/VisibilityOffOutlined";
import ArrowForwardIcon            from "@mui/icons-material/ArrowForward";
import ArrowBackIcon               from "@mui/icons-material/ArrowBack";

function Login() {
  const [email,         setEmail]         = useState("");
  const [password,      setPassword]      = useState("");
  const [showPass,      setShowPass]      = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [error,         setError]         = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorMsg = params.get("error");
    if (errorMsg) {
      setError(decodeURIComponent(errorMsg));
      window.history.replaceState({}, "", "/login");
    }
    const token = localStorage.getItem("token");
    const user = (() => {
      try { return JSON.parse(localStorage.getItem("user")); }
      catch { return null; }
    })();
    if (token && user?.role?.name === "admin") navigate("/admin");
    else if (token && user?.role?.name) navigate("/");
  }, []);

  const getFriendlyError = (err) => {
    const status = err.response?.status;
    let msg = "";
    
    if (err.response?.data) {
      if (typeof err.response.data === "string") {
        msg = err.response.data;
      } else if (err.response.data.message) {
        msg = err.response.data.message;
      } else if (err.response.data.error) {
        msg = err.response.data.error;
      }
    }

    if (status === 422) {
      const errors = err.response?.data?.errors;
      if (errors) {
        const firstKey = Object.keys(errors)[0];
        return errors[firstKey]?.[0] ?? "Data tidak valid.";
      }
      return msg || "Data tidak valid.";
    }
    if (status === 403) return "Akun tidak aktif. Hubungi administrator.";
    if (status === 401) return "Email atau password salah.";
    if (err.message === "Network Error" || err.code === "ECONNABORTED") {
      return "Gagal terhubung ke server (Port 8000). Pastikan firewall tidak memblokir port 8000.";
    }
    if (typeof msg === "string" && msg.trim() !== "") {
      if (msg.includes("syntax error") || msg.includes("Exception") || msg.includes("SQLSTATE")) {
        return "Terjadi kesalahan pada server. Coba beberapa saat lagi.";
      }
      return msg;
    }
    return err.message || "Terjadi kesalahan. Coba beberapa saat lagi.";
  };

  const handleLogin = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setError(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    if (!email || !password) { setError("Email dan password wajib diisi."); return; }
    try {
      setLoading(true);
      const response = await api.post("/login", { email: email.trim(), password });
      const { token, user } = response.data;
      const role = user?.role?.name;
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      const redirectTo = localStorage.getItem("redirect_after_login");
      localStorage.removeItem("redirect_after_login");
      if (role === "superadmin") {
        navigate(redirectTo || "/superadmin");
      } else if (role === "admin") {
        navigate(redirectTo || "/admin");
      } else {
        navigate(redirectTo || "/");
      }
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setLoadingGoogle(true); setError(null);
      const response = await api.get("/auth/google");
      window.location.href = response.data.url;
    } catch {
      setError("Gagal menghubungkan ke Google.");
      setLoadingGoogle(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "7px", fontSize: "0.8rem",
      "&:hover fieldset": { borderColor: "#9ca3af" },
      "&.Mui-focused fieldset": { borderColor: "#111827", borderWidth: "1.5px" },
    },
  };

  return (
    <Box sx={{
      minHeight: "100vh", display: "flex",
      alignItems: "center", justifyContent: "center",
      position: "relative", overflow: "hidden",
    }}>
      <Box sx={{
        position: "absolute", inset: 0,
        backgroundImage: "url('https://images.unsplash.com/photo-1562774053-701939374585?w=1600&q=80')",
        backgroundSize: "cover", backgroundPosition: "center",
        filter: "blur(6px) brightness(0.45)", transform: "scale(1.05)",
      }} />
      <Box sx={{
        position: "absolute", inset: 0,
        background: "linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(17,24,39,0.55) 100%)",
      }} />

      {/* Card — lebih kecil */}
      <Box sx={{
        position: "relative", zIndex: 1, width: "100%", maxWidth: 340,
        backgroundColor: "rgba(255,255,255,0.97)", borderRadius: "16px",
        p: { xs: 2.5, sm: 3 },
        boxShadow: "0 20px 50px rgba(0,0,0,0.35)", mx: 2,
      }}>

        {/* Logo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 3 }}>
          <Box component="img" src="/lpkia-logo2.png" alt="LPKIA"
            sx={{ height: 28, width: "auto", objectFit: "contain" }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", color: "#111827", lineHeight: 1.2 }}>
              BAS-LPKIA
            </Typography>
            <Typography sx={{ fontSize: "0.58rem", color: "#6b7280", lineHeight: 1.2 }}>
              Sistem Pengadaan
            </Typography>
          </Box>
        </Box>

        {/* Kembali */}
        <Box onClick={() => navigate("/")} sx={{
          display: "flex", alignItems: "center", gap: 0.4,
          color: "#9ca3af", cursor: "pointer", mb: 2, width: "fit-content",
          "&:hover": { color: "#111827" }, transition: "color 0.15s",
        }}>
          <ArrowBackIcon sx={{ fontSize: 13 }} />
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 500 }}>Kembali</Typography>
        </Box>

        <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: "#111827", letterSpacing: "-0.02em", mb: 0.3 }}>
          Selamat Datang
        </Typography>
        <Typography sx={{ fontSize: "0.78rem", color: "#6b7280", mb: 2.5 }}>
          Masuk ke sistem pengadaan LPKIA
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}
            sx={{ mb: 2, borderRadius: "7px", fontSize: "0.75rem", py: 0.5 }}>
            {error}
          </Alert>
        )}

        {/* Google */}
        <Button type="button" fullWidth variant="outlined"
          onClick={handleGoogleLogin} disabled={loadingGoogle || loading}
          startIcon={
            loadingGoogle
              ? <CircularProgress size={14} />
              : <Box component="img"
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  sx={{ width: 15, height: 15 }} />
          }
          sx={{
            borderRadius: "7px", textTransform: "none", fontSize: "0.78rem",
            fontWeight: 600, py: 0.9, borderColor: "#e5e7eb", color: "#374151", mb: 2,
            "&:hover": { borderColor: "#9ca3af", backgroundColor: "#f9fafb" },
          }}>
          {loadingGoogle ? "Menghubungkan..." : "Masuk dengan Google"}
        </Button>

        <Divider sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: "0.68rem", color: "#9ca3af", px: 1 }}>atau</Typography>
        </Divider>

        {/* Email */}
        <Box sx={{ mb: 1.8 }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#374151", mb: 0.6 }}>Email</Typography>
          <TextField fullWidth size="small" placeholder="nama@lpkia.ac.id"
            value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleLogin(e); } }}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <EmailOutlinedIcon sx={{ fontSize: 15, color: "#9ca3af" }} />
              </InputAdornment>
            )}}
            sx={fieldSx} />
        </Box>

        {/* Password */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#374151", mb: 0.6 }}>Password</Typography>
          <TextField fullWidth size="small" type={showPass ? "text" : "password"}
            placeholder="••••••••" value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleLogin(e); } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ fontSize: 15, color: "#9ca3af" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPass(!showPass)} edge="end">
                    {showPass
                      ? <VisibilityOffOutlinedIcon sx={{ fontSize: 15, color: "#9ca3af" }} />
                      : <VisibilityOutlinedIcon    sx={{ fontSize: 15, color: "#9ca3af" }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={fieldSx} />
        </Box>

        {/* Submit */}
        <Button type="button" fullWidth variant="contained"
          onClick={(e) => handleLogin(e)} disabled={loading || loadingGoogle}
          endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: 15 }} />}
          sx={{
            borderRadius: "7px", textTransform: "none", fontSize: "0.82rem", fontWeight: 700,
            py: 1.1, backgroundColor: "#111827", boxShadow: "none",
            "&:hover": { backgroundColor: "#1f2937", boxShadow: "0 4px 14px rgba(0,0,0,0.15)" },
            "&:disabled": { backgroundColor: "#e5e7eb" },
            transition: "all 0.2s",
          }}>
          {loading ? <CircularProgress size={16} sx={{ color: "#9ca3af" }} /> : "Masuk"}
        </Button>

        <Typography sx={{ fontSize: "0.72rem", color: "#6b7280", textAlign: "center", mt: 2 }}>
          Belum punya akun?{" "}
          <Box component="span" onClick={() => navigate("/register")}
            sx={{ color: "#111827", fontWeight: 700, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
            Daftar di sini
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}

export default Login;